import { Link } from "react-router";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  Link2,
  User,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  describeProposedCall,
  proposalAge,
  targetName,
  type AgentWriteProposal,
  type ProposalOutcome,
} from "./proposalReview";

/**
 * The proposal review card — a sibling of SwipeCard.tsx, not a variant of it.
 * A contact and a proposal are different shapes (a contact IS a record to
 * judge; a proposal is a CALL to judge, made about a record it merely
 * points at), so this is its own component rather than SwipeCard grown a
 * second mode. The chrome — rounded card, header/body/footer, backdrop
 * dimming — deliberately echoes SwipeCard's so the two decks read as one
 * family.
 */
export const ProposalCard = ({
  proposal,
  outcome,
  isBackdrop = false,
  now,
}: {
  proposal: AgentWriteProposal;
  /** Set once approve/reject has returned for this exact card. */
  outcome?: ProposalOutcome;
  className?: string;
  isBackdrop?: boolean;
  now: Date;
}) => {
  const name = targetName(proposal);
  const { toolLabel, lines } = describeProposedCall(proposal);
  const age = proposalAge(proposal.created_at, now);

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-2xl border bg-card shadow-lg",
        isBackdrop && "pointer-events-none",
      )}
    >
      <div className="flex shrink-0 items-start gap-3 border-b p-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg leading-tight font-semibold">
            {name ?? "No target record yet"}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" aria-hidden />
              {proposal.actor}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" aria-hidden />
              {age} ago
            </span>
          </div>
        </div>
        <Badge variant="secondary" className="shrink-0 font-mono text-[11px]">
          {toolLabel}
        </Badge>
      </div>

      {/* Same clip-and-fade treatment as SwipeCard: a fixed card height keeps
          the answer buttons in one place across cards, so long evidence gets
          clipped rather than pushing the layout around. */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.25rem),transparent)]">
        {outcome ? (
          <OutcomePanel outcome={outcome} />
        ) : (
          <>
            <Field label="Would do" value={proposal.draft_question} />
            <Field label="Because" value={proposal.reason} />
            {proposal.suggested_next_step && (
              <Field
                label="Suggested next step"
                value={proposal.suggested_next_step}
              />
            )}
            {lines.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {toolLabel} arguments
                </p>
                <dl className="mt-1 space-y-0.5">
                  {lines.map((line) => (
                    <div key={line.label} className="flex gap-2 text-sm">
                      <dt className="shrink-0 text-muted-foreground">
                        {line.label}:
                      </dt>
                      <dd className="min-w-0 truncate">{line.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {proposal.source_ref && (
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground italic">
                <Link2 className="h-3 w-3 shrink-0" aria-hidden />
                {proposal.source_ref}
              </p>
            )}
          </>
        )}
      </div>

      {!isBackdrop && proposal.target_id != null && (
        <div className="flex shrink-0 items-center gap-4 border-t px-4 py-2 text-xs">
          <Link
            to={`/${proposal.target_type === "company" ? "companies" : "contacts"}/${proposal.target_id}/show`}
            className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            {proposal.target_type === "company" ? (
              <Building2 className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <User className="h-3.5 w-3.5" aria-hidden />
            )}
            Full record
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
      {label}
    </p>
    <p className="text-sm leading-snug">{value}</p>
  </div>
);

/**
 * What actually happened, in place of the proposal's own content. Nothing
 * here is optimistic — this only renders once a real response has come back,
 * including a failed one; see useProposalQueue's comment on why a card holds
 * its ground until this is dismissed.
 */
const OutcomePanel = ({ outcome }: { outcome: ProposalOutcome }) => {
  switch (outcome.kind) {
    case "approved":
      return (
        <OutcomeBanner
          tone="positive"
          icon={<CheckCircle2 className="h-5 w-5" aria-hidden />}
          title="Approved and written"
          detail={outcome.speech}
        />
      );
    case "approved_with_drift":
      return (
        <OutcomeBanner
          tone="warning"
          icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
          title="Approved and written — but the target moved"
          detail={outcome.speech}
        >
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
            {outcome.drift.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </OutcomeBanner>
      );
    case "rejected":
      return (
        <OutcomeBanner
          tone="neutral"
          icon={<XCircle className="h-5 w-5" aria-hidden />}
          title="Rejected"
          detail={outcome.speech}
        />
      );
    case "failed":
      return (
        <OutcomeBanner
          tone="negative"
          icon={<XCircle className="h-5 w-5" aria-hidden />}
          title="Did not go through"
          detail={outcome.speech}
        />
      );
    case "not_claimed":
      return (
        <OutcomeBanner
          tone="neutral"
          icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
          title={`Already ${outcome.status}`}
          detail={outcome.speech}
        />
      );
    case "error":
      return (
        <OutcomeBanner
          tone="negative"
          icon={<XCircle className="h-5 w-5" aria-hidden />}
          title="Request failed"
          detail={outcome.speech}
        />
      );
  }
};

const OUTCOME_TONE: Record<string, string> = {
  positive:
    "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  warning:
    "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  negative:
    "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  neutral: "border-muted-foreground/30 bg-muted text-foreground",
};

const OutcomeBanner = ({
  tone,
  icon,
  title,
  detail,
  children,
}: {
  tone: "positive" | "warning" | "negative" | "neutral";
  icon: React.ReactNode;
  title: string;
  detail: string;
  children?: React.ReactNode;
}) => (
  <div className={cn("rounded-lg border p-3", OUTCOME_TONE[tone])}>
    <p className="flex items-center gap-2 text-sm font-semibold">
      {icon}
      {title}
    </p>
    <p className="mt-1 text-sm leading-snug">{detail}</p>
    {children}
  </div>
);
